import React, { useEffect, useRef, useState } from 'react';
import Layout from '../components/Layout';
import { Link } from 'react-router-dom';
import api, { fileUrl } from '../lib/api';
import { Radio, Users, ThumbsUp, Flame, Crown, TrendingUp, Trophy } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const avatarFallback = (name) =>
  `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'U')}&background=FFB000&color=000&bold=true&size=200`;

// Rank medal palette — #1 gold, #2 silver, #3 bronze, everyone else dim.
const RANK_STYLES = [
  { ring: 'ring-2 ring-[#FFB000]',            badge: 'bg-[#FFB000] text-black', label: '#1' },
  { ring: 'ring-2 ring-[#C0C0C0]/70',         badge: 'bg-[#C0C0C0] text-black', label: '#2' },
  { ring: 'ring-2 ring-[#CD7F32]/70',         badge: 'bg-[#CD7F32] text-black', label: '#3' },
];

export default function LiveFeed() {
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deltas, setDeltas] = useState({}); // stream_id -> {delta, ts}
  const prevVotesRef = useRef({});

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get('/live/active');
        if (cancelled) return;
        const next = r.data || [];
        const now = Date.now();
        // Compute vote deltas vs the previous poll for each stream
        const bumps = {};
        for (const s of next) {
          const prev = prevVotesRef.current[s.stream_id];
          if (prev !== undefined && s.votes > prev) {
            bumps[s.stream_id] = { delta: s.votes - prev, ts: now };
          }
          prevVotesRef.current[s.stream_id] = s.votes;
        }
        setStreams(next);
        // Merge new bumps with existing deltas, then expire anything older than 4s.
        setDeltas(prev => {
          const merged = { ...prev, ...bumps };
          for (const sid of Object.keys(merged)) {
            if (now - merged[sid].ts > 4000) delete merged[sid];
          }
          return merged;
        });
        setLoading(false);
      } catch {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const t = setInterval(load, 5000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // The stream at rank 0 is "currently winning"
  const leader = streams[0];

  return (
    <Layout>
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#EF4444] mb-2 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#EF4444] animate-pulse" /> Live now
          </div>
          <h1 className="font-heading font-black text-white text-4xl md:text-5xl tracking-tighter">Who's winning?</h1>
          <p className="text-[#A1A1AA] text-sm mt-2 max-w-lg">Every DJ live on RAW MIXES right now — tap any tile to jump in and vote. Leaderboard updates every 5 seconds.</p>
        </div>
      </div>

      {loading && <div className="text-[#A1A1AA] text-sm py-10 text-center">Checking who's live…</div>}
      {!loading && streams.length === 0 && (
        <div className="post-card p-10 text-center" data-testid="live-feed-empty">
          <Radio size={32} className="mx-auto mb-3 text-[#FFB000]/50" />
          <div className="text-white font-heading font-bold">No one's live yet</div>
          <div className="text-[#A1A1AA] text-sm mt-1">Head to Live Decks and hit Go Live — everyone will land right here.</div>
          <Link to="/decks" className="btn-pill btn-primary inline-flex mt-4" data-testid="live-feed-empty-decks">Open Decks</Link>
        </div>
      )}

      {!loading && leader && (
        <div className="post-card p-5 mb-6 border-[#FFB000]/40 bg-gradient-to-br from-[#FFB000]/10 via-transparent to-transparent" data-testid="live-feed-leader">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="w-14 h-14 rounded-full bg-[#FFB000] flex items-center justify-center flex-shrink-0">
              <Crown size={24} className="text-black" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[#FFB000] text-[10px] uppercase tracking-[0.3em] font-black">Currently winning</div>
              <div className="text-white font-heading font-black text-xl truncate">{leader.name} — {leader.title}</div>
              <div className="text-[#A1A1AA] text-xs mt-0.5 flex items-center gap-3">
                <span className="flex items-center gap-1"><ThumbsUp size={11} className="text-[#22C55E]" /> {leader.votes} votes</span>
                <span className="flex items-center gap-1"><Users size={11} /> {leader.viewer_count} listening</span>
              </div>
            </div>
            <Link to={`/live/${leader.stream_id}`} className="btn-pill btn-primary text-sm flex items-center gap-2" data-testid="live-feed-leader-cta">
              <Trophy size={14} /> Watch
            </Link>
          </div>
        </div>
      )}

      {!loading && streams.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="live-feed-grid">
          {streams.map((s, idx) => {
            const rank = RANK_STYLES[idx]; // undefined past rank 3
            const delta = deltas[s.stream_id];
            return (
              <Link
                key={s.stream_id}
                to={`/live/${s.stream_id}`}
                className={`post-card p-0 overflow-hidden group hover:border-[#FFB000]/40 transition-colors relative ${rank ? rank.ring : ''}`}
                data-testid={`live-tile-${s.stream_id}`}
              >
                <div className="relative aspect-video bg-gradient-to-b from-[#1a1a1a] via-black to-black flex items-center justify-center overflow-hidden">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
                    className="w-32 h-32 rounded-full bg-black border-2 border-[#FFB000]/60 flex items-center justify-center"
                  >
                    <div className="w-10 h-10 rounded-full bg-[#FFB000]" />
                  </motion.div>
                  <div className="absolute top-2 left-2 flex items-center gap-1.5">
                    <div className="px-2 py-0.5 rounded-full bg-[#EF4444] text-white text-[9px] font-black uppercase tracking-widest flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> LIVE
                    </div>
                    {rank && (
                      <div className={`px-2 py-0.5 rounded-full ${rank.badge} text-[9px] font-black tracking-widest`} data-testid={`live-tile-rank-${s.stream_id}`}>
                        {rank.label}
                      </div>
                    )}
                  </div>
                  {s.is_battle && (
                    <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-[#FFB000] text-black text-[9px] font-black uppercase tracking-widest flex items-center gap-1">
                      <Flame size={10} /> Battle
                    </div>
                  )}
                  {/* Vote-delta pop */}
                  <AnimatePresence>
                    {delta && (
                      <motion.div
                        key={delta.ts}
                        initial={{ opacity: 0, y: 12, scale: 0.8 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -18, scale: 0.9 }}
                        transition={{ duration: 0.4 }}
                        className="absolute bottom-2 right-2 px-2 py-1 rounded-full bg-[#22C55E] text-black text-[10px] font-black uppercase tracking-widest flex items-center gap-1 shadow-lg"
                        data-testid={`live-tile-delta-${s.stream_id}`}
                      >
                        <TrendingUp size={11} /> +{delta.delta}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <div className="p-4 flex items-center gap-3">
                  <img
                    src={s.avatar_path ? fileUrl(s.avatar_path) : avatarFallback(s.name)}
                    alt=""
                    className="w-10 h-10 rounded-full object-cover border border-[#FFB000]/40 flex-shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-white text-sm font-heading font-bold truncate group-hover:text-[#FFB000] transition-colors">{s.title}</div>
                    <div className="text-[#A1A1AA] text-xs truncate">@{s.username}</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-[10px] text-[#A1A1AA] uppercase tracking-widest font-bold flex items-center gap-1"><Users size={10} /> {s.viewer_count}</div>
                    <div className="text-[10px] text-[#22C55E] uppercase tracking-widest font-bold flex items-center gap-1 mt-0.5" data-testid={`live-tile-votes-${s.stream_id}`}>
                      <ThumbsUp size={10} /> {s.votes}
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </Layout>
  );
}
